/**
 * Games domain — TanStack Query keys, fetchers, and mutations.
 */
import { apiFetch } from '../../lib/api/client.js';

export const GAMES_QUERY_KEYS = {
  providers: ['games', 'providers'] as const,
  list: (params: { providerId?: string; gameType?: string; tag?: string; category?: string }) =>
    ['games', 'list', params] as const,
} as const;

// ── Provider ──────────────────────────────────────────────────────────────────

export type LaunchMode = 'iframe' | 'redirect' | 'popup' | 'internal';
export type LaunchMethod = 'GET' | 'POST';

export interface GameProvider {
  id: string;
  code: string;
  displayName: string;
  logoUrl: string | null;
  type: string;
  launchMode: LaunchMode;
  status: 'active' | 'inactive';
  brandColor?: string | null;
  categories?: string[];
}

export interface PaginatedProviders {
  items: GameProvider[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 200 is svc-game's own CLAMP on this route, not a number chosen here: it is
 * player-reachable and resolves the per-node disabled gate for every item, so a
 * wider page multiplies that fan-out (`apps/game/src/routes/providers.ts`).
 * Asking for more does not 422 — it silently serves 200 — so the loop below is
 * what covers a platform with more camps than one page holds.
 */
const PROVIDER_PAGE_SIZE = 200;
/**
 * A stop, not a budget. It only bounds a server that echoes the same page
 * forever; 5,000 active camps is already an order of magnitude past anything
 * this platform carries.
 */
const PROVIDER_MAX_PAGES = 25;

/**
 * Every active provider, paged until the catalog is exhausted.
 *
 * It used to ask for one page of 50 and stop. Nothing said so — the caller got a
 * `PaginatedProviders` that looked complete — and the promotion play lock reads
 * this list to turn a promotion's allowed-camp ids into a verdict per card. A
 * camp past the cut resolved to "no provider row", which reads as "not locked",
 * so a whole camp's games quietly stopped showing the lock and opened into a
 * server refusal instead (#524).
 *
 * The page-full test is the primary signal. `total` is consulted too because
 * svc-game's disabled-gate branch filters AFTER paging and reports the filtered
 * count, so a short page is not always the last one.
 */
export async function fetchProviders(): Promise<PaginatedProviders> {
  const items: GameProvider[] = [];
  let total = 0;

  for (let page = 1; page <= PROVIDER_MAX_PAGES; page++) {
    const reply = await apiFetch<PaginatedProviders>(
      `/providers?page=${page}&pageSize=${PROVIDER_PAGE_SIZE}&status=active`,
    );
    const batch = reply.items ?? [];
    items.push(...batch);
    total = Math.max(total, reply.total ?? 0, items.length);

    const pageWasFull = batch.length >= PROVIDER_PAGE_SIZE;
    const totalSaysMore = items.length < total;
    if (!pageWasFull && !totalSaysMore) break;
    if (batch.length === 0) break;
  }

  return { items, total: Math.max(total, items.length), page: 1, pageSize: items.length };
}

// ── Game ──────────────────────────────────────────────────────────────────────

export interface Game {
  id: string;
  providerId: string;
  code: string;
  name: string;
  imageUrl: string | null;
  thumbnailUrl?: string | null;
  // Raw `metadata.layers`: may be absent or malformed, so it is only ever read
  // through parseGameLayers (@b1fr0st/contracts/game-layers).
  layers?: unknown;
  // Raw `metadata.tile`, the game's Tile from the art library; read only
  // through parseGameTile (components/lobby/tileImage.ts).
  tile?: unknown;
  // Raw `metadata.themeTiles`, the game's Theme cards by theme code; read only
  // through pickThemeTile / cardTile (components/lobby/tileImage.ts).
  themeTiles?: unknown;
  gameType: string;
  tag: 'new' | 'hot' | null;
  rtp: number | null;
  launchCode: string;
  sortOrder: number;
  status: 'active' | 'inactive';
  providerCode?: string;
  providerDisplayName?: string;
  // Upstream brand (Gamesway sub-provider, AMB product, SUPERM sub-provider); see features/games/brandBadge.ts.
  brandCode?: string | null;
  // Provider launch dispatch — present on catalog responses, lets the FE
  // decide iframe vs redirect vs popup at click time.
  launchMode?: LaunchMode;
  launchMethod?: LaunchMethod;
  // Lottery fields — populated only when category === 'lottery'.
  category?: string | null;
  lotteryType?: string | null;
  lotteryStatus?: string | null;
  ticketBefore?: string | null; // close time (ปิดรับ)
  ticketAfter?: string | null;  // open time (เปิดรับ)
  roundDate?: string | null;
}

export interface PaginatedGames {
  items: Game[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchGames(params: {
  providerId?: string;
  gameType?: string;
  tag?: string;
  category?: string;
  pageSize?: number;
  page?: number;
}): Promise<PaginatedGames> {
  const qs = new URLSearchParams();
  if (params.providerId) qs.set('providerId', params.providerId);
  if (params.gameType) qs.set('gameType', params.gameType);
  if (params.tag) qs.set('tag', params.tag);
  if (params.category) qs.set('category', params.category);
  qs.set('pageSize', String(params.pageSize ?? 50));
  if (params.page) qs.set('page', String(params.page));
  qs.set('status', 'active');
  // Bound the catalog request so a hung gateway surfaces as a retryable query
  // error instead of an infinite loading skeleton on the game-browse pages.
  return apiFetch<PaginatedGames>(`/games?${qs.toString()}`, { signal: AbortSignal.timeout(15_000) });
}

/**
 * Fetch the entire active catalog for a filter, paging past the backend's 200/page
 * clamp. Used by /casino, which needs every game (lottery alone is ~100+ rows).
 */
export async function fetchAllGames(
  params: { providerId?: string; gameType?: string; tag?: string; category?: string } = {},
): Promise<Game[]> {
  const out: Game[] = [];
  for (let page = 1; page <= 6; page++) {
    // ponytail: 6×200 = 1200-game ceiling; raise the bound if a real catalog exceeds it.
    const res = await fetchGames({ ...params, pageSize: 200, page });
    out.push(...res.items);
    if (out.length >= res.total || res.items.length < 200) break;
  }
  return out;
}

// ── Launch ────────────────────────────────────────────────────────────────────

export interface LaunchResult {
  mode: LaunchMode;
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
  expiresAt: string;
}

export function launchGameMutation(args: {
  providerId: string;
  gameId?: string;
  lang?: string;
  returnUrl?: string;
}): Promise<LaunchResult> {
  return apiFetch<LaunchResult>('/launch', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  });
}
